import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import "./ProjectsView.scss";
import { userAssignmentClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { TaskUpdateRequest } from "../../api-client-nswag/create-sites-client";
import { UserAssignmentListDto } from "../../api-client-nswag/userassignment-client";
import type { TaskRow } from "./TasksGrid";
import { useSnackbar } from "../../hooks";
import { SnackbarMessage } from "../../hooks/useSnackbar.types";
import { processTaskUpdateResponse, showTaskUpdateSnackbar, formatAssigneeName } from "./taskUpdateUtils";

export const TASK_STATUS_OPTIONS = ["New", "Ready to Start", "In Progress", "On Hold", "Done", "N/A"];

export type TaskEditResult = { id: string; status?: string; assignedTo?: string; assignedEmail?: string };

type EditTaskDialogProps = {
  open: boolean;
  onClose: () => void;
  siteId: number;
  tasks: TaskRow[];
  onSaved: (updates: TaskEditResult[], failedIds?: string[]) => void;
};

const EditTaskDialog: React.FC<EditTaskDialogProps> = ({ open, onClose, siteId, tasks, onSaved }) => {
  const [status, setStatus] = useState<string>("");
  const [assigneeEmail, setAssigneeEmail] = useState<string>("");
  const [assignees, setAssignees] = useState<UserAssignmentListDto[]>([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (!open) return;
    setError(null);
    setStatus(tasks.length === 1 ? tasks[0]?.status || "" : "");
    setAssigneeEmail("");
    setLoadingAssignees(true);
    userAssignmentClientApi
      .userAssignment_GetUserAssignmentList(siteId || null, null, null, null)
      .then((resp: any) => {
        const list = resp?.data?.data ?? [];
        setAssignees(Array.isArray(list) ? list : []);
      })
      .catch(() => setAssignees([]))
      .finally(() => setLoadingAssignees(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siteId]);

  const getAssigneeLabel = (a: UserAssignmentListDto) => {
    const name = `${a.firstName || ""} ${a.lastName || ""}`.trim();
    return name ? `${name} (${a.userEmail || ""})` : a.userEmail || "";
  };

  const handleSave = async () => {
    if (!status && !assigneeEmail) return;
    setSaving(true);
    setError(null);
    try {
      const chosen = assigneeEmail ? assignees.find((a) => a.userEmail === assigneeEmail) : undefined;
      const chosenName = formatAssigneeName(chosen);

      const requestTasks: TaskUpdateRequest[] = tasks.map((t) => {
        const upd: TaskUpdateRequest = { taskId: t.numericTaskId ?? 0 };
        if (status) upd.status = status;
        if (assigneeEmail) {
          upd.assigned = chosenName;
          upd.assignedEmail = assigneeEmail;
          upd.assignedEmployeeNumber = chosen?.userId ?? chosen?.userId ?? undefined;
        }
        return upd;
      });

      const response = await createSitesClientApi.taxportal_UpdateTask(siteId, { tasks: requestTasks });
      const { updatedRows, failedRows, successMessages, errorMessages } = processTaskUpdateResponse(response, tasks);

      // Notify the parent even when nothing succeeded, so it can still reset the selection
      // and highlight the failed rows red.
      onSaved(
        updatedRows.map(({ row, result }) => ({
          id: row.id,
          status: result.status ?? row.status,
          assignedTo: result.assigned ?? row.assignedTo,
          assignedEmail: result.assigned ? (result.assignedEmail ?? "") : undefined,
        })),
        failedRows.map(({ row }) => row.id),
      );
      showTaskUpdateSnackbar(showSnackbar, successMessages, errorMessages);
      onClose();
    } catch {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{tasks.length > 1 ? `Edit Tasks (${tasks.length})` : "Edit Task"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl size="small" fullWidth>
            <InputLabel id="edit-task-status-label">Status</InputLabel>
            <Select
              labelId="edit-task-status-label"
              label="Status"
              value={status}
              onChange={(e) => setStatus(String(e.target.value))}
              renderValue={(selected) => {
                if (!selected) return <em>No change</em>;
                const statusClass = String(selected || "").toLowerCase().replace(/\s+/g, "-");
                return (
                  <Chip label={String(selected)} size="small" variant="outlined" className={`chip status ${statusClass}`} />
                );
              }}
            >
              <MenuItem value="">
                <em>No change</em>
              </MenuItem>
              {TASK_STATUS_OPTIONS.map((opt) => {
                const statusClass = String(opt || "").toLowerCase().replace(/\s+/g, "-");
                return (
                  <MenuItem key={opt} value={opt}>
                    <Chip label={opt} size="small" variant="outlined" className={`chip status ${statusClass}`} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel id="edit-task-assignee-label">Assignee</InputLabel>
            <Select
              labelId="edit-task-assignee-label"
              label="Assignee"
              value={assigneeEmail}
              onChange={(e) => setAssigneeEmail(String(e.target.value))}
              disabled={loadingAssignees}
              endAdornment={loadingAssignees ? <CircularProgress size={16} sx={{ mr: 2 }} /> : undefined}
            >
              <MenuItem value="">
                <em>No change</em>
              </MenuItem>
              {assignees.map((a) => (
                <MenuItem key={a.userEmail || a.userId} value={a.userEmail || ""}>
                  {getAssigneeLabel(a)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (!status && !assigneeEmail)}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTaskDialog;
